import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Minus, Plus, ShoppingBag, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../contexts/CartContext';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductRow = ({ product, quantity, onQuantityChange }) => {
  return (
    <div 
      className="flex flex-col sm:flex-row sm:items-center justify-between p-5 bg-white rounded-xl border border-gray-100 hover:border-[#E8945A]/30 transition-colors"
      data-testid={`product-row-${product.id}`}
    >
      <div className="flex-1 mb-4 sm:mb-0">
        <div className="flex items-start gap-3">
          <div className="w-2 h-2 rounded-full bg-[#E8945A] mt-2 flex-shrink-0"></div>
          <div>
            <h3 className="font-['Playfair_Display'] font-semibold text-lg text-[#2D3E50]">
              {product.name}
            </h3>
            <p className="text-sm text-[#64748B] mt-1 whitespace-pre-line">
              {product.description}
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-6 sm:gap-8">
        {/* Quantity selector */}
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9 rounded-lg border-gray-200"
            onClick={() => onQuantityChange(product.id, Math.max(0, quantity - 1))}
            data-testid={`decrease-${product.id}`}
          >
            <Minus className="h-4 w-4" />
          </Button>
          <span className="w-8 text-center font-semibold text-[#2D3E50]" data-testid={`qty-${product.id}`}>
            {quantity}
          </span>
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9 rounded-lg border-gray-200"
            onClick={() => onQuantityChange(product.id, quantity + 1)}
            data-testid={`increase-${product.id}`}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        {/* Price */}
        <div className="text-right min-w-[80px]">
          <span className="text-xl font-semibold text-[#2D3E50]">
            {product.price.toFixed(2)} €
          </span>
        </div>
      </div>
    </div>
  );
};

const ProductsPage = () => {
  const navigate = useNavigate();
  const { addItem, items: cartItems } = useCart();
  const { toast } = useToast();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantities, setQuantities] = useState({});

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
        // Initialize quantities to 0
        const initialQuantities = {};
        response.data.forEach(p => {
          initialQuantities[p.id] = 0;
        });
        setQuantities(initialQuantities);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const handleQuantityChange = (productId, newQuantity) => {
    setQuantities(prev => ({
      ...prev,
      [productId]: newQuantity
    }));
  };

  const handleAddToCart = () => {
    let addedCount = 0;
    products.forEach(product => {
      const qty = quantities[product.id];
      if (qty > 0) {
        addItem(product, qty);
        addedCount += qty;
      }
    });

    if (addedCount > 0) {
      toast({
        title: "Ajouté au panier",
        description: `${addedCount} article${addedCount > 1 ? 's' : ''} ajouté${addedCount > 1 ? 's' : ''} au panier.`,
      });
      // Reset quantities
      const resetQuantities = {};
      products.forEach(p => {
        resetQuantities[p.id] = 0;
      });
      setQuantities(resetQuantities);
    } else {
      toast({
        title: "Sélectionnez des articles",
        description: "Veuillez sélectionner au moins un article.",
        variant: "destructive",
      });
    }
  };

  const totalSelected = Object.values(quantities).reduce((sum, qty) => sum + qty, 0);
  const totalPrice = products.reduce((sum, product) => {
    return sum + (product.price * (quantities[product.id] || 0));
  }, 0);

  // Group products by category
  const kitsCouette = products.filter(p => p.category === 'kits-couette');
  const lingeToilette = products.filter(p => p.category === 'linge-de-toilette');

  return (
    <div className="min-h-screen pt-20 md:pt-24 bg-[#FAFAFA]">
      {/* Header */}
      <section className="bg-[#2D3E50] py-12 md:py-16 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-white mb-4">
            Composez votre commande
          </h1>
          <p className="text-white/80 text-lg">
            Sélectionnez les articles dont vous avez besoin pour votre séjour
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 md:px-12 py-12">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="h-8 w-8 animate-spin text-[#E8945A]" />
          </div>
        ) : (
          <>
            {/* Kits Draps */}
            {kitsCouette.length > 0 && (
              <div className="mb-10">
                <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#2D3E50] mb-4 flex items-center gap-2">
                  <span className="w-1 h-6 bg-[#E8945A] rounded-full"></span>
                  Kits Draps
                </h2>
                <div className="space-y-3">
                  {kitsCouette.map(product => (
                    <ProductRow
                      key={product.id}
                      product={product}
                      quantity={quantities[product.id] || 0}
                      onQuantityChange={handleQuantityChange}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Linge de toilette */}
            {lingeToilette.length > 0 && (
              <div className="mb-10">
                <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#2D3E50] mb-4 flex items-center gap-2">
                  <span className="w-1 h-6 bg-[#E8945A] rounded-full"></span>
                  Linge de Toilette
                </h2>
                <div className="space-y-3">
                  {lingeToilette.map(product => (
                    <ProductRow
                      key={product.id}
                      product={product}
                      quantity={quantities[product.id] || 0}
                      onQuantityChange={handleQuantityChange}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Summary & Add to Cart */}
            <div className="sticky bottom-6 mt-12">
              <div className="bg-white rounded-xl border border-gray-200 shadow-lg p-6">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-6">
                    <div>
                      <p className="text-sm text-[#64748B]">Articles sélectionnés</p>
                      <p className="text-2xl font-semibold text-[#2D3E50]">{totalSelected}</p>
                    </div>
                    <div className="w-px h-10 bg-gray-200"></div>
                    <div>
                      <p className="text-sm text-[#64748B]">Total</p>
                      <p className="text-2xl font-semibold text-[#E8945A]">{totalPrice.toFixed(2)} €</p>
                    </div>
                  </div>
                  <Button
                    size="lg"
                    className="bg-[#E8945A] hover:bg-[#D4844A] rounded-lg h-14 px-8 text-base w-full sm:w-auto"
                    onClick={handleAddToCart}
                    disabled={totalSelected === 0}
                    data-testid="add-all-to-cart"
                  >
                    <ShoppingBag className="mr-2 h-5 w-5" />
                    Ajouter au panier
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProductsPage;
